import type { SelfDay } from '@/services/attendanceService';

// What the office check-in card says and offers — pure, so the one rule that
// matters is tested rather than read off JSX.
//
// ⚠️ On a SCANNED day the day's clock_out is the scanner's last punch ("last
// seen"), not a check-out. Reading it as one told people who had badged in
// "Done for today" and hid the button they needed. Only the person's own
// check-out (self_out) finishes their day here.
export type SelfCardAction = 'check_in' | 'check_out' | 'none';

export interface SelfCardState {
  action: SelfCardAction;
  key: string;
  defaultValue: string;
  values?: Record<string, string>;
  /** net minutes to show once the day is finished, else undefined */
  net?: number;
}

/**
 * Whether the card is offered at all. ⚠️ ANTI BUDDY-PUNCHING: only when self
 * check-in is actually allowed for this person's office — a self check-in
 * office, or a scanner office where an admin turned the BACKUP on. Never a
 * Check-in button the server would refuse.
 */
export const selfCardVisible = (
  mode: string | null | undefined,
  backup: boolean | null | undefined,
  openSession?: boolean | null,
): boolean =>
  mode === 'self_checkin' || (mode === 'biometric' && backup === true) || openSession === true;

/**
 * ⚠️ QA B8 (2026-10-24): turning backup off while someone was checked in
 * through it hid the card, so they could never check out. A session they
 * already OPENED can always be closed: the card stays, offering only Check
 * out. It never offers Check in — that still needs self check-in allowed.
 */
export const closingOnly = (
  mode: string | null | undefined,
  backup: boolean | null | undefined,
  openSession?: boolean | null,
): boolean => openSession === true && !(mode === 'self_checkin' || (mode === 'biometric' && backup === true));

export const selfCardState = (
  day: SelfDay | null | undefined,
  opts: { backup?: boolean; closingOnly?: boolean } = {},
): SelfCardState => {
  if (opts.closingOnly) {
    return {
      action: 'check_out', key: 'att.self_closing_only',
      defaultValue: 'Self check-in is now off for your office. You checked in at {{in}} — check out here when you leave.',
      values: { in: day?.self_in || day?.clock_in || '' },
    };
  }
  if (!day || !day.clock_in) {
    return opts.backup
      ? { action: 'check_in', key: 'att.self_backup_hint', defaultValue: 'Scanner not working? Check in here as a backup — HR will see it.' }
      : { action: 'check_in', key: 'att.self_hint', defaultValue: 'Your office has no scanner — record your presence here.' };
  }
  if (day.scanned) {
    const first = day.scanner_first || day.clock_in;
    if (day.self_out) {
      return {
        action: 'none', key: 'att.self_scanned_done',
        defaultValue: 'Scanner recorded you from {{first}} · you checked out at {{out}} · {{net}}',
        values: { first, out: day.self_out }, net: day.net_minutes,
      };
    }
    return {
      action: 'check_out', key: 'att.self_scanned_in',
      defaultValue: 'Scanner recorded you at {{first}} — you can still check out when you leave.',
      values: { first },
    };
  }
  if (day.clock_out) {
    return {
      action: 'none', key: 'att.self_done',
      defaultValue: 'Done for today: {{in}} → {{out}} · {{net}}',
      values: { in: day.clock_in, out: day.clock_out }, net: day.net_minutes,
    };
  }
  return {
    action: 'check_out', key: 'att.self_in_since',
    defaultValue: 'Checked in at {{in}} — check out when you leave.',
    values: { in: day.clock_in },
  };
};
